import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ArticleService } from 'app/library/article/services/article.service';
import { ContentNodeState } from 'app/library/content-node/models/content-node.state';
import { CourseCatalogLandingPageService } from 'app/library/course-catalog-landing-page/services/course-catalog-landing-page.service';
import { CourseOfferingLandingPageService } from 'app/library/course-offering-catalog/services/course-offering-landing-page.service';
import { GroupService } from 'app/library/group/service/group.service';
import { ModuleGroupService } from 'app/library/module-group/service/module-group.service';
import { ModuleService } from 'app/library/module/services/module.service';
import { SectionService } from 'app/library/section/services/section.service';
import { UserService } from 'app/library/user/services/user.service';
import { IContextListItem } from './general-analytics.service';

@Injectable({
    providedIn: 'root',
})
export class SharedAnalyticsService {
    private _currentUserId: string;

    private _module([id, ...urlSegments]: string[]): IContextListItem[] {
        if (!id) {
            return [];
        }

        const edit_mode = urlSegments[0] === 'edit';

        if (edit_mode) {
            urlSegments.splice(0, 1);
        }

        const module = this._moduleService.getModuleById(id);

        if (!module) {
            return [];
        }

        const { name, isPrivate } = module;

        if (!this._currentUserId) {
            console.warn('Missing current user id', this._currentUserId);
        }

        const module_type = urlSegments[0] === 'e-learning' ? (urlSegments.splice(0, 1)[0] as 'e-learning') : isPrivate ? 'private' : 'default';

        return [{ type: 'module', name, id, edit_mode, module_type }, ...this._moduleTabs(urlSegments)];
    }

    private _groups([id, ...urlSegments]: string[]): IContextListItem[] {
        if (!id) {
            return [];
        }

        const { name } = this._groupService.getGroupById(id);

        return [{ type: 'group', name, id }, ...this._groupTabs(urlSegments)];
    }

    private _engage([name, ...urlSegments]: string[]): IContextListItem[] {
        const articleId = urlSegments[1];

        if (!articleId) {
            return [];
        }

        const { translation } = this._articleService.getArticleById(articleId);

        return [{ type: 'article', name: translation.title, id: articleId }];
    }

    private _admin([name, ...urlSegments]: string[]): IContextListItem[] {
        let params: IContextListItem[] = [];

        switch (name) {
            case 'users':
                params = this._user(urlSegments);
                break;
        }

        return [{ type: 'tab', name }, ...params];
    }

    private _userTabs([name]: string[]): IContextListItem[] {
        return [{ type: 'tab', name }];
    }

    private _profile([firstSegment, ...urlSegments]: string[]): IContextListItem[] {
        switch (firstSegment) {
            case 'tab': {
                const tabName = urlSegments[0];

                return [{ type: 'page', name: tabName || 'general' }];
            }
            case 'to-dos':
                return [{ type: 'page', name: 'to-dos' }];
            case 'performance-review':
                return [{ type: 'page', name: 'performance-review' }];
            case 'certificates':
                return [{ type: 'page', name: 'certificates' }];
            case 'approval-requests':
                return [{ type: 'page', name: 'approval-requests' }];
            default:
                return [];
        }
    }

    private _user([id, ...urlSegments]: string[]): IContextListItem[] {
        if (!id) {
            return [];
        }

        const edit_mode = urlSegments[0] === 'edit';

        if (edit_mode) {
            urlSegments.splice(0, 1);
        }

        const { fullName: name } = this._userService.getUserById(id);

        return [{ type: 'user', name, id, edit_mode }, ...this._userTabs(urlSegments)];
    }

    private _moduleTabs([name, ...urlSegments]: string[]): IContextListItem[] {
        let params: IContextListItem[] = [];

        switch (name) {
            case 'collaboration':
                // the tab url without a group/node segment is a valid intermediate state, the tab guard redirects to one afterwards
                const nextCollaborationUrlSegment = urlSegments.splice(0, 1)[0];

                switch (nextCollaborationUrlSegment) {
                    case 'groups':
                        params = this._moduleGroups(urlSegments);
                        break;
                    default:
                        params = this._nodesWrapper(nextCollaborationUrlSegment, urlSegments);
                        break;
                }
                break;
            case 'content':
                const nextContentUrlSegment = urlSegments.splice(0, 1)[0];

                switch (nextContentUrlSegment) {
                    case 'sections':
                        params = this._sections(urlSegments);
                        break;
                    default:
                        params = this._nodesWrapper(nextContentUrlSegment, urlSegments);
                        break;
                }
                break;
        }

        return [{ type: 'tab', name }, ...params];
    }

    private _moduleGroups([id, ...urlSegments]: string[]): IContextListItem[] {
        if (!id) {
            return [];
        }

        let name: string;

        if (id === 'all') {
            name = 'General module group'; // TODO: ask FRank!
        } else {
            const moduleGroup = this._moduleGroupService.getModuleGroupById(id);
            name = moduleGroup.name;
        }

        return [{ type: 'group', name, id }, ...this._groupTabs(urlSegments)];
    }

    private _nodesWrapper(name: string, urlSegments: string[]) {
        switch (name) {
            case 'node':
                return this._nodes(urlSegments, 'folder');
            case 'file':
                return this._nodes(urlSegments, 'file');
            default:
                return [];
        }
    }

    private _sections([id, section_type, ...urlSegments]: string[]): IContextListItem[] {
        if (!id) {
            return [];
        } else if (!section_type) {
            // throw new Error('Invalid context: Section route must provide the section type togeather with the section id!');
        }

        let params: IContextListItem[] = this._nodesWrapper(section_type, urlSegments);

        const section = this._sectionService.getSectionById(id);

        if (!section) {
            return params;
        }

        const { name } = section;

        let toReturn = [{ type: 'section', name, id, section_type }, ...params];

        const isReportPage = urlSegments.some((segment) => segment === 'report');

        if (isReportPage) {
            toReturn = [{ type: 'section', name, id, section_type }, { name: 'report', type: 'report' }, ...params];
        }

        return toReturn;
    }

    private _catalog(urlSegments: string[]): IContextListItem[] {
        const context: IContextListItem[] = [];

        const isCourseOfferingOverview = urlSegments[0] === 'course-offerings' && urlSegments[2] === 'landing-page' && urlSegments[3] === 'overview';

        if (!isCourseOfferingOverview) {
            context.push({
                type: 'catalog_landing_page',
                name: 'catalog',
            });
        }

        if (urlSegments[0] === 'category' && urlSegments[1]) {
            const categoryId = urlSegments[1];
            const overview = this._courseCatalogLandingPageService.overview;

            if (overview) {
                context.push({
                    type: 'category_landing_page',
                    name: overview.landingPage.title,
                    id: categoryId,
                });
            }
        }

        if (isCourseOfferingOverview) {
            const overview = this._courseOfferingLandingPageService.overview;

            if (overview) {
                context.push({
                    type: 'course_offering_landing_page',
                    name: overview.title,
                    id: overview.id,
                });
            }
        }

        return context;
    }

    private _groupTabs([name, ...urlSegments]: string[]): IContextListItem[] {
        return [{ type: 'tab', name: name === 'file' ? 'node' : name }, ...this._nodesWrapper(name, urlSegments)];
    }

    private _nodes(urlSegments: string[], type: 'file' | 'folder'): IContextListItem[] {
        const id = urlSegments[0];

        if (id === 'root') {
            return [{ type, name: id }];
        }

        const contentNode = ContentNodeState.getEntity(this._store.selectSnapshot(ContentNodeState), id);

        if (!contentNode) {
            return [{ type, name: id, id }];
        }

        const { name, breadcrumbs } = contentNode;

        const breadcrumbNames = breadcrumbs.map(({ name: breadcrumbName }) => breadcrumbName);

        let index = breadcrumbNames.indexOf('Sections');

        if (index !== -1) {
            index++;
        }

        if (index === -1) {
            index = breadcrumbNames.indexOf('Course_content');
        }

        if (index === -1) {
            index = breadcrumbNames.indexOf('Groups') + 1;
        }

        const relevantBreadcrubs = breadcrumbNames.slice(index + 1);

        const node_path = relevantBreadcrubs.length ? `/${relevantBreadcrubs.join('/')}/` : `/`;

        return [{ type, name, id, node_path }];
    }

    constructor(
        private _store: Store,
        private _moduleService: ModuleService,
        private _moduleGroupService: ModuleGroupService,
        private _sectionService: SectionService,
        private _userService: UserService,
        private _groupService: GroupService,
        private _articleService: ArticleService,
        private _courseCatalogLandingPageService: CourseCatalogLandingPageService,
        private _courseOfferingLandingPageService: CourseOfferingLandingPageService
    ) {
        const userProfile = JSON.parse(localStorage.getItem('userProfile'));
        if (userProfile) {
            this._currentUserId = userProfile.id;
        }
    }

    generateLocationContext([name, ...urlSegments]: string[]): IContextListItem[] {
        let params: IContextListItem[] = [];

        switch (name) {
            case 'modules':
                params = this._module(urlSegments);
                break;
            case 'groups':
                params = this._groups(urlSegments);
                break;
            case 'grow':
                params = this._engage(urlSegments);
                break;
            case 'admin':
                params = this._admin(urlSegments);
                break;
            case 'catalog':
                params = this._catalog(urlSegments);
                break;
            case 'profile':
                params = this._profile(urlSegments);
                break;
        }

        return [{ type: 'top_navigation_tab', name }, ...params];
    }
}
